import { useMemo } from 'react';

import { differenceInYears, isBefore, isSameDay } from 'date-fns';

import type { Visit } from '../../../../../@types/visits';
import { useVisits } from '../../../../../utils/hooks/useVisits';

const filterVisitsByDate = (visit: any, appointmentOn: string) => {
  if (!visit.visitDate) return false;

  const visitDate = new Date(visit.visitDate);
  const appointmentDate = new Date(appointmentOn);
  const yearDiff = Math.abs(differenceInYears(visitDate, appointmentDate));

  return (
    yearDiff <= 1 &&
    (isSameDay(visitDate, appointmentDate) ||
      isBefore(visitDate, appointmentDate))
  );
};

const getVisitWithFmx = (visits: Visit[], defaultVisitId?: string) => {
  if (defaultVisitId) {
    return visits.find((visit) => visit.id === Number(defaultVisitId));
  }

  const sortedVisits = visits.sort((a, b) => {
    return (
      new Date(b.visitDate || '').getTime() -
      new Date(a.visitDate || '').getTime()
    );
  });

  for (const visit of sortedVisits) {
    if (
      visit.imageTypeCounts &&
      visit.imageTypeCounts.some(
        (tc) => tc.type === 'BITEWING' && Number(tc.count) >= 4,
      )
    ) {
      return visit;
    }
  }

  return null;
};

export const useClaimVisit = (input: {
  imagingPatientId: string | undefined;
  appointmentOn?: string | null;
  defaultVisitId?: string;
}) => {
  const { imagingPatientId, appointmentOn, defaultVisitId } = input;

  const { allVisits, isLoading } = useVisits(imagingPatientId);

  const visits = useMemo(
    () =>
      appointmentOn
        ? allVisits.filter((visit) => filterVisitsByDate(visit, appointmentOn))
        : allVisits,
    [allVisits, appointmentOn],
  );

  const visitWithFmx = useMemo(
    () => getVisitWithFmx(visits, defaultVisitId),
    [visits, defaultVisitId],
  );

  return {
    estimatedVisitId: visitWithFmx?.id ?? null,
    imagingPatientId: visitWithFmx?.patientVideaId ?? null,
    visitDate: visitWithFmx?.visitDate?.toString() ?? null,
    isLoading,
    visits,
  };
};
